import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { logError } from "@/lib/logger";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageCropperSheet from "@/components/chat/ImageCropperSheet";
import { Check, UploadSimple, Star, PencilSimple, ChatCircleDots, Camera, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import AutocompleteInput from "@/components/AutocompleteInput";
import SkillsChipInput from "@/components/SkillsChipInput";
import ReferAndEarn from "@/components/ReferAndEarn";
import PasskeyManager from "@/components/PasskeyManager";
import { ProfileEnrichmentSection, KycSection } from "@/components/ProfileEnrichment";
import { LOCATION_SUGGESTIONS } from "@/lib/suggestions";
import { IN_CITIES } from "@/lib/indiaGeo";
import { smartPasteHandler, reformatPastedText } from "@/lib/smartPaste";

const WORK_MODES = [
    { value: "Any", label: "Any", desc: "Open to all" },
    { value: "Onsite", label: "Onsite", desc: "Prefer office" },
    { value: "Hybrid", label: "Hybrid", desc: "Mix of both" },
    { value: "Remote", label: "Remote", desc: "Work from anywhere" },
];

const FIELDS = [
    ["name", "Full name", "text"],
    ["phone", "Phone", "text"],
    ["current_title", "Current title", "text"],
    ["years_experience", "Years experience", "number"],
    ["linkedin", "LinkedIn URL", "url"],
];

const T_STATUS = {
    pending: { label: "Pending review", cls: "bg-[#FEF3C7] text-[#B45309]" },
    approved: { label: "Live", cls: "bg-[#ECFDF5] text-[#059669]" },
    hidden: { label: "Hidden", cls: "bg-[#F5F5F5] text-[#525252]" },
};

export default function CandidateProfile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState(null);
    const [skills, setSkills] = useState([]);
    const [saving, setSaving] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");
    // V2.0.53 (Feb 26, 2026) — id returned by /public/resumes/parse
    // when the candidate uploads a PDF. Sent with the next profile
    // save so the raw file is attached to the user account, then
    // inherited by every future job apply.
    const [pendingUploadId, setPendingUploadId] = useState(null);
    const [pendingUploadName, setPendingUploadName] = useState("");
    // Batch 3 (Feb 2026) — inline testimonials block on profile.
    const [tData, setTData] = useState(null);
    const [tRequests, setTRequests] = useState([]);

    const load = useCallback(() => {
        api.get("/me/profile")
            .then((r) => {
                setForm(r.data);
                setSkills(Array.isArray(r.data.skills) ? r.data.skills : []);
            })
            .catch((e) => logError("Profile load failed:", e));
    }, []);

    useEffect(() => {
        load();
        // Fetch inline testimonials + pending requests. Silent-fail on error.
        api.get("/me/candidate/testimonials")
            .then((r) => setTData(r.data))
            .catch(() => {});
        api.get("/me/candidate/testimonial-requests")
            .then((r) => setTRequests(r.data || []))
            .catch(() => {});
    }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSaved(false);
        setSaving(true);
        try {
            const payload = {
                ...form,
                years_experience: Number(form.years_experience) || 0,
                skills: skills,
                // V2.0.53 — attach the raw PDF stashed at parse-time.
                upload_id: pendingUploadId || undefined,
            };
            delete payload.id;
            delete payload.email;
            delete payload.role;
            delete payload.created_at;
            // resume_url/resume_filename/resume_size are read-only
            // response fields — never send them back on PUT.
            delete payload.resume_url;
            delete payload.resume_filename;
            delete payload.resume_size;
            delete payload.resume_updated_at;
            const r = await api.put("/me/profile", payload);
            setForm(r.data);
            setUser({ ...user, name: r.data.name });
            setSaved(true);
            setPendingUploadId(null);
            setPendingUploadName("");
            setTimeout(() => setSaved(false), 2000);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePdf = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        setPdfLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/resumes/parse`, {
                method: "POST",
                body: fd,
            });
            const data = await r.json();
            if (!r.ok) throw new Error(formatApiError(data.detail));
            const parsedText = reformatPastedText(data.text || "");
            // V2.0.53 — block image-only / OCR-empty CVs upfront.
            if (parsedText.trim().length < 30) {
                throw new Error(
                    "We couldn't read enough text from your CV — it may be an image-only PDF. " +
                    "Please paste your resume below, or try a different file."
                );
            }
            setForm((f) => ({ ...f, resume_text: parsedText }));
            // V2.0.53 — remember the upload id so the next profile save
            // attaches the raw PDF to the account. Cleared after a
            // successful PUT.
            setPendingUploadId(data.upload_id || null);
            setPendingUploadName(data.filename || file.name || "");
        } catch (ex) {
            setErr(ex.message || "Could not parse resume");
        } finally {
            setPdfLoading(false);
            e.target.value = "";
        }
    };

    if (!form) return <div className="px-4 sm:px-8 py-10 text-[#525252]">Loading…</div>;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-3xl mx-auto" data-testid="candidate-profile-page">
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Your details · reused on every apply
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Profile.
                </h1>
            </div>

            {/* Completion bar */}
            {typeof form.completion_pct === "number" && (
                <div
                    data-testid="profile-completion-card"
                    className="bg-white border border-[#E5E5E5] p-4 sm:p-5 mb-4"
                >
                    <div className="flex items-baseline justify-between mb-2">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                            Profile strength
                        </p>
                        <span
                            className={`font-heading font-black text-2xl tracking-tighter ${
                                form.completion_pct >= 70
                                    ? "text-[#059669]"
                                    : form.completion_pct >= 40
                                      ? "text-[#F59E0B]"
                                      : "text-[#DC2626]"
                            }`}
                        >
                            {form.completion_pct}%
                        </span>
                    </div>
                    <div className="h-2 bg-[#F5F5F5] relative">
                        <div
                            className={`absolute inset-y-0 left-0 transition-all duration-700 ${
                                form.completion_pct >= 70
                                    ? "bg-[#059669]"
                                    : form.completion_pct >= 40
                                      ? "bg-[#F59E0B]"
                                      : "bg-cobalt"
                            }`}
                            style={{ width: `${form.completion_pct}%` }}
                        />
                    </div>
                    {form.missing_fields?.length > 0 && (
                        <p className="text-xs text-[#525252] mt-2">
                            Add{" "}
                            <span className="font-bold text-ink">
                                {form.missing_fields.map((f) => f.replace("_", " ")).join(", ")}
                            </span>{" "}
                            to reach 100%.
                        </p>
                    )}
                </div>
            )}

            <AvatarSection
                avatarUrl={form.avatar_url}
                name={form.name}
                onChange={(url) => setForm((f) => ({ ...f, avatar_url: url }))}
            />

            <form onSubmit={submit} className="bg-white border border-[#E5E5E5] p-5 sm:p-8 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {FIELDS.map(([k, label, type]) => (
                        <div key={k}>
                            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                {label}
                            </label>
                            <input
                                data-testid={`profile-${k}-input`}
                                type={type}
                                value={form[k] ?? ""}
                                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                            />
                        </div>
                    ))}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                            Location
                        </label>
                        <AutocompleteInput
                            testId="profile-location-input"
                            field="location"
                            staticOptions={[...new Set([...IN_CITIES, ...(LOCATION_SUGGESTIONS || [])])]}
                            value={form.location ?? ""}
                            onChange={(v) => setForm({ ...form, location: v })}
                            placeholder="e.g. Bengaluru, Mumbai, Remote"
                        />
                    </div>
                </div>

                {/* V1.5.5.11 — Work mode preference. Powers recruiter's
                    'Remote-friendly' filter chip + future auto-matching. */}
                <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                        Preferred work mode
                    </label>
                    <div
                        role="radiogroup"
                        aria-label="Preferred work mode"
                        data-testid="profile-work-mode-preference"
                        className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                    >
                        {WORK_MODES.map((wm) => {
                            const active = (form.work_mode_preference || "") === wm.value;
                            return (
                                <button
                                    type="button"
                                    key={wm.value}
                                    role="radio"
                                    aria-checked={active}
                                    data-testid={`profile-work-mode-${wm.value.toLowerCase()}`}
                                    onClick={() => setForm({ ...form, work_mode_preference: wm.value })}
                                    className={`text-left p-3 border transition-colors ${
                                        active
                                            ? "border-cobalt bg-[#EFF6FF] ring-1 ring-cobalt"
                                            : "border-[#E5E5E5] bg-white hover:border-[#A3A3A3]"
                                    }`}
                                >
                                    <p className={`text-sm font-bold ${active ? "text-cobalt" : "text-ink"}`}>
                                        {wm.label}
                                    </p>
                                    <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                                        {wm.desc}
                                    </p>
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-2 text-[10px] text-[#A3A3A3]">
                        Recruiters will see this chip on your card. Skip if you don&apos;t want to signal a preference.
                    </p>
                </div>
                <div>
                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                        Skills
                    </label>
                    <SkillsChipInput
                        testId="profile-skills"
                        value={skills}
                        onChange={setSkills}
                        placeholder="React, TypeScript, Node.js…"
                    />
                </div>
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                            Resume
                        </label>
                        <label
                            data-testid="profile-upload-pdf-label"
                            className="text-xs font-bold uppercase tracking-wider text-cobalt cursor-pointer inline-flex items-center gap-1 hover:underline"
                        >
                            <UploadSimple size={12} weight="bold" />
                            {pdfLoading ? "Parsing…" : "Upload resume"}
                            <input
                                data-testid="profile-pdf-input"
                                type="file"
                                accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                                className="hidden"
                                onChange={handlePdf}
                                disabled={pdfLoading}
                            />
                        </label>
                    </div>
                    <textarea
                        data-testid="profile-resume-input"
                        value={typeof form.resume_text === "string" ? form.resume_text : ""}
                        rows={10}
                        onChange={(e) => setForm({ ...form, resume_text: e.target.value })}
                        onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, resume_text: v })))}
                        placeholder="Paste your resume or upload a file above…"
                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-sm focus:outline-none focus:border-cobalt whitespace-pre-wrap"
                    />
                    {/* V2.0.53 (Feb 26, 2026) — "CV on file" state so the
                        candidate knows their PDF is attached to the account
                        and will be sent along with every future apply. */}
                    {(pendingUploadName || form.resume_filename) && (
                        <div
                            data-testid="profile-cv-attached"
                            className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1"
                        >
                            <span aria-hidden>📎</span>
                            {pendingUploadName ? (
                                <span>
                                    New CV ready — save profile to attach:{" "}
                                    <span className="normal-case font-normal text-emerald-800">
                                        {pendingUploadName}
                                    </span>
                                </span>
                            ) : (
                                <span>
                                    CV on file — recruiters can download:{" "}
                                    <span className="normal-case font-normal text-emerald-800">
                                        {form.resume_filename}
                                    </span>
                                </span>
                            )}
                        </div>
                    )}
                    <p className="text-[10px] text-[#A3A3A3] mt-1">
                        Upload PDF, DOCX, JPG or PNG — we auto-extract text (OCR for scanned resumes).
                    </p>
                </div>

                {err && (
                    <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                        {err}
                    </div>
                )}

                <div className="flex items-center gap-3">
                    <button
                        data-testid="profile-save-button"
                        type="submit"
                        disabled={saving}
                        className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm transition-colors"
                    >
                        {saving ? "Saving…" : "Save profile"}
                    </button>
                    {saved && (
                        <span
                            data-testid="profile-saved-indicator"
                            className="inline-flex items-center gap-1 text-sm font-bold text-[#059669]"
                        >
                            <Check size={14} weight="bold" /> Saved
                        </span>
                    )}
                </div>
            </form>

            {/* Batch 3 (Feb 2026) — Testimonials inline on profile */}
            {(tData || tRequests.length > 0) && (
                <div className="mt-10" data-testid="profile-testimonials-section">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                                My testimonials
                            </p>
                            <h2 className="font-heading text-2xl sm:text-3xl font-black tracking-tighter text-ink">
                                Reviews you&apos;ve shared.
                            </h2>
                        </div>
                        <Link
                            to="/candidate/testimonials"
                            data-testid="profile-testimonials-see-all"
                            className="text-xs font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark"
                        >
                            Manage all →
                        </Link>
                    </div>

                    {/* Pending nudges from recruiters */}
                    {tRequests.length > 0 && (
                        <div className="mb-4 space-y-2" data-testid="profile-testimonial-requests">
                            {tRequests.map((r) => (
                                <div
                                    key={r.id}
                                    data-testid={`profile-request-${r.id}`}
                                    className="bg-[#FFFBEB] border border-[#FDE68A] p-4 flex items-start gap-3"
                                >
                                    <ChatCircleDots size={18} weight="fill" className="text-[#B45309] shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-ink">
                                            {r.requested_by_name} asked for your review of{" "}
                                            <span className="text-cobalt">{r.client?.name}</span>
                                        </p>
                                        <p className="text-xs text-[#525252] mt-0.5">
                                            {r.can_submit
                                                ? "You can leave a testimonial now."
                                                : "Unlocks automatically once you're Hired at this client."}
                                        </p>
                                        {r.note && (
                                            <p className="text-xs text-[#B45309] italic mt-1">&ldquo;{r.note}&rdquo;</p>
                                        )}
                                    </div>
                                    {r.can_submit && (
                                        <Link
                                            to="/candidate/testimonials"
                                            data-testid={`profile-request-submit-${r.id}`}
                                            className="text-xs font-black uppercase tracking-wider bg-cobalt hover:bg-cobalt-dark text-white px-3 py-2 whitespace-nowrap"
                                        >
                                            Write review
                                        </Link>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Submitted testimonials */}
                    {tData && tData.submitted && tData.submitted.length > 0 ? (
                        <div className="space-y-3">
                            {tData.submitted.slice(0, 3).map((t) => {
                                const meta = T_STATUS[t.status] || T_STATUS.pending;
                                return (
                                    <div
                                        key={t.id}
                                        data-testid={`profile-testimonial-${t.id}`}
                                        className="bg-white border border-[#E5E5E5] p-4 sm:p-5"
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <div className="min-w-0">
                                                <p className="text-xs font-black uppercase tracking-wider text-[#A3A3A3]">
                                                    {t.client_name}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {[1, 2, 3, 4, 5].map((n) => (
                                                        <Star
                                                            key={n}
                                                            size={12}
                                                            weight={n <= t.rating ? "fill" : "regular"}
                                                            className={n <= t.rating ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span
                                                    data-testid={`profile-testimonial-status-${t.id}`}
                                                    className={`text-[10px] font-bold px-2 py-0.5 ${meta.cls}`}
                                                >
                                                    {meta.label}
                                                </span>
                                                <Link
                                                    to="/candidate/testimonials"
                                                    data-testid={`profile-testimonial-edit-${t.id}`}
                                                    title="Edit — resubmitting sends for re-approval"
                                                    className="text-[#525252] hover:text-ink"
                                                >
                                                    <PencilSimple size={14} weight="bold" />
                                                </Link>
                                            </div>
                                        </div>
                                        <p className="text-sm text-[#525252] leading-relaxed line-clamp-3">
                                            &ldquo;{t.text}&rdquo;
                                        </p>
                                        {t.status === "approved" && (
                                            <p className="text-[10px] text-[#059669] mt-2">
                                                Note: editing this will send it back for admin re-approval.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-[#F9FAFB] border border-dashed border-[#E5E5E5] p-6 text-center text-sm text-[#525252]">
                            You haven&apos;t left a testimonial yet. Once you&apos;re Hired at a client,
                            you can share a short review from your{" "}
                            <Link to="/candidate/testimonials" className="text-cobalt font-bold">
                                Testimonials page
                            </Link>.
                        </div>
                    )}
                </div>
            )}

            {/* Refer & Earn widget — same one that lives on the landing page,
                shown here so the referral link is always one tap away. */}
            <ProfileEnrichmentSection initial={{ employment: form?.employment, education: form?.education }} />
            <KycSection />

            <div className="mt-6" data-testid="profile-refer-and-earn">
                <ReferAndEarn variant="portal" />
            </div>

            <div className="mt-6">
                <PasskeyManager />
            </div>
        </div>
    );
}

// ---------- Avatar section (Option D — optional; hidden from recruiter list ----
//              view by default to reduce unconscious bias) ---------------------
function AvatarSection({ avatarUrl, name, onChange }) {
    const [busy, setBusy] = useState(false);
    const [preview, setPreview] = useState(avatarUrl || null);
    const [pendingFile, setPendingFile] = useState(null);  // V1.9.34 — cropper input
    const inputRef = useRef(null);

    useEffect(() => { setPreview(avatarUrl || null); }, [avatarUrl]);

    const initials =
        (name || "")
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0])
            .join("")
            .toUpperCase() || "?";

    const onFile = (file) => {
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("JPEG, PNG or WEBP only.");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Max 5 MB. Please compress and try again.");
            return;
        }
        // V1.9.34 — open the cropper instead of uploading raw.
        setPendingFile(file);
    };

    const onCropped = async (blob) => {
        setPendingFile(null);
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "avatar.png");
            const r = await api.post("/me/profile/avatar", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPreview(r.data?.avatar_url || null);
            onChange?.(r.data?.avatar_url || null);
            toast.success("Photo added.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await api.delete("/me/profile/avatar");
            setPreview(null);
            onChange?.(null);
            toast.success("Photo removed.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't remove photo.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            id="photo"
            data-testid="profile-avatar-section"
            className="bg-white border border-[#E5E5E5] p-5 sm:p-6 mb-4"
        >
            <div className="flex items-center gap-4">
                <div
                    data-testid="profile-avatar-preview"
                    className="w-20 h-20 sm:w-24 sm:h-24 bg-cobalt text-white font-black text-2xl sm:text-3xl flex items-center justify-center overflow-hidden shrink-0"
                >
                    {preview ? (
                        <img
                            src={preview}
                            alt="Your profile"
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        initials
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                        Optional · you own this
                    </p>
                    <h2 className="font-heading text-xl sm:text-2xl font-black tracking-tight text-ink">
                        Profile photo
                    </h2>
                    <p className="text-[11px] text-[#525252] mt-1 leading-relaxed">
                        Recruiters won&apos;t see your photo in the search list by default — only
                        inside your full profile — to keep hiring bias-free.
                    </p>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <label className="cursor-pointer">
                            <input
                                ref={inputRef}
                                data-testid="profile-avatar-input"
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                className="hidden"
                                onChange={(e) => onFile(e.target.files?.[0])}
                                disabled={busy}
                            />
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider bg-ink text-white hover:opacity-90 disabled:opacity-50">
                                <Camera size={12} weight="bold" />
                                {busy ? "Uploading…" : preview ? "Change photo" : "Add photo"}
                            </span>
                        </label>
                        {preview && (
                            <button
                                type="button"
                                data-testid="profile-avatar-remove"
                                onClick={remove}
                                disabled={busy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border border-[#E5E5E5] text-[#DC2626] hover:border-[#DC2626] disabled:opacity-50"
                            >
                                <Trash size={12} weight="bold" /> Remove
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <ImageCropperSheet
                file={pendingFile}
                onCancel={() => setPendingFile(null)}
                onCropped={onCropped}
                title="Adjust profile photo"
            />
        </div>
    );
}
